// app/frontend/components/ui/Inputs/ThemedSwitch.tsx
import React from 'react';
import { Switch, type SwitchProps } from '@mantine/core';
import { COLORS } from '../../../src/constants/theme';
import { themeVar } from '../../../src/utils/pageTheme';
import '../../../styles/ThemedInputs.css';

export interface ThemedSwitchProps extends SwitchProps {
  /**
   * 'inline' (default): toggle, then label, description indented under the label.
   * 'row': a settings row. Label and description start at the left edge, lined
   * up with the headings and cards around them, and the toggle sits at the right.
   */
  layout?: 'inline' | 'row';
}

const ThemedSwitch = React.forwardRef<HTMLInputElement, ThemedSwitchProps>(
  // The page theme's accent on a themed page (e.g. the Dance Card editor, #2550), WCWD orange elsewhere
  ({ color = themeVar('accent'), styles, layout = 'inline', ...props }, ref) => {
    const row = layout === 'row';
    return (
      <Switch
        ref={ref}
        color={color}
        labelPosition={row ? 'left' : props.labelPosition}
        styles={{
          ...(row
            ? {
                root: { width: '100%' },
                // Toggle centred on the title + description block, not pinned
                // to the top-right corner of a two-line row.
                body: { justifyContent: 'space-between', alignItems: 'center', gap: 16, paddingInline: 0 },
                labelWrapper: { paddingInline: 0, flex: 1, minWidth: 0 },
                track: { flexShrink: 0 },
              }
            : {}),
          label: { color: '#f2f2f2', cursor: 'pointer', fontWeight: 500, ...(row ? { paddingInline: 0 } : {}) },
          // Top margin comes from .wcwd-switch-description (ThemedInputs.css).
          // #d1d1d1 at 400: #999 at 300 measured 4.4:1 on the soft setting
          // panel and 6.1:1 on a card, short of 7:1 for 12px text.
          description: {
            color: COLORS.textDescription,
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.5,
            paddingLeft: row ? 0 : 2,
          },
          ...(typeof styles === 'object' ? styles : {}),
        }}
        classNames={{ description: 'wcwd-switch-description' }}
        {...props}
      />
    );
  }
);

ThemedSwitch.displayName = 'ThemedSwitch';

export default ThemedSwitch;
